import { FindPlaceCandidate } from "../apis/google";
import { useEffect, useRef, useState, useTransition } from "react";
import InputOptions from "./InputOptions";
import TextField from "./TextField";
import { APIs } from "../apis";

export type GooglePlacesSearchProps = {
  label: string;
  placeholder: string;
  onSelect: (place: FindPlaceCandidate) => void;
  className?: string;
  value?: string;
};

export default function GooglePlacesSearch({
  label,
  placeholder,
  value,
  onSelect,
  className,
}: GooglePlacesSearchProps) {
  const [places, setPlaces] = useState<FindPlaceCandidate[]>([]);
  const [isPending, startTransition] = useTransition();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && value) {
      inputRef.current.value = value;
    }
  }, [value]);

  return (
    <InputOptions
      classNames={{ menu: "min-w-[250px]" }}
      onChange={(_, option) =>
        onSelect(places.find((p) => p.id === option.value)!)
      }
      options={places.map((place) => ({
        value: place.id,
        label: place.formattedAddress ?? place.displayName?.text ?? "",
        icon: (
          <img
            className="w-[10px] mt-[5px]"
            src={`${place.iconMaskBaseUri}.png`}
            alt={
              place.formattedAddress ?? place.displayName?.text ?? ""
            }
          />
        ),
      }))}
    >
      <TextField
        ref={inputRef}
        variant="surface"
        label={label}
        className={className}
        placeholder={placeholder}
        onChange={async ({ target }) => {
          if (target.value.length < 3) {
            setPlaces([]);
            return;
          }
          startTransition(async () => {
            try {
              const response = await APIs.google.searchPlaces(target.value);
              setPlaces(response?.places ?? []);
            } catch {
              setPlaces([]);
            }
          });
        }}
        endAddon={
          isPending ? (
            <div className="ml-2 flex h-4 w-4 items-center justify-center">
              <div
                aria-label="Loading"
                className="h-4 w-4 animate-spin rounded-full border-2 border-text-primary/20 border-t-text-primary/70"
              />
            </div>
          ) : null
        }
      />
    </InputOptions>
  );
}
